// ═══════════════════════════════════════════════════════════════════
// RAKHI SALE 2026 — Main Script (duplicated from birthday-sale.js, adapted)
// Runs on TWO collections (fragrance + non-fragrance) — see RAKHI_COLLECTIONS
// below for the per-collection qty/price/premium-tag config.
// ═══════════════════════════════════════════════════════════════════
//
// FLOW:
//   ATC click (existing <add-to-cart>) → drawer opens (untouched)
//   ajaxProduct:added event → show qty selector on that card
//   + click → qty++ + open drawer
//   - click → qty-- silently | qty=0 → show ATC back
//
// LIVE:
//   updateProgressBars()   → collection page fixed bar (JS) + mini-cart (Liquid re-render)
//   updatePremiumCap()     → hide premium/signature ATC cards when cap (1) hit
// ═══════════════════════════════════════════════════════════════════

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────
  // Rakhi Sale 2026 — single source of truth for the two collection
  // handles, injected by theme.liquid from Theme Settings → Rakhi Sale
  // (settings.rakhi_fragrance_collection / rakhi_nonfragrance_collection).
  // Falls back to the current test handles when window.RAKHI_HANDLES isn't
  // present (e.g. settings not configured yet), so this is a
  // zero-behavior-change refactor until someone picks the production
  // collections in Theme Settings.
  // ─────────────────────────────────────────────────────────────────
  var RAKHI_HANDLES_CFG = window.RAKHI_HANDLES || {};
  var RAKHI_FRAGRANCE_HANDLE = RAKHI_HANDLES_CFG.fragrance || 'fragrance-rakhi-sale-2026-test';
  var RAKHI_NONFRAGRANCE_HANDLE = RAKHI_HANDLES_CFG.nonFragrance || 'non-fragrance-rakhi-sale-2026-test';

  // ─────────────────────────────────────────────────────────────────
  // CONFIG — change values here, not in logic below
  // ─────────────────────────────────────────────────────────────────
  var BS_CONFIG = {
    // Rakhi runs on TWO collections (fragrance + non-fragrance). Derive the
    // current collection handle from the URL so the product map fetches the
    // right collection on whichever sale page we're on.
    collectionHandle: (function () {
      var m = window.location.pathname.match(/collections\/([^/?#]+)/);
      return m ? m[1] : RAKHI_FRAGRANCE_HANDLE;
    })()
  };

  // ─────────────────────────────────────────────────────────────────
  // COLLECTION-KEYED CONFIG — unlike Birthday Sale (one collection, two
  // bundle tiers 2/4), Rakhi Sale is ONE flat bundle per collection, but
  // TWO different collections share this same script. RAKHI_CFG resolves
  // to the right qty/price/label for whichever page we're on.
  //
  // NOTE: the backend tag/metafield is ALWAYS 'premium' on both collections
  // (confirmed by user) — 'Signature' on non-fragrance is a UI-only label,
  // not a different tag. So premium DETECTION (.plp-badge-premium / tag
  // 'premium') stays the same on both pages; only badgeLabel changes what
  // text the badge shows.
  // ─────────────────────────────────────────────────────────────────
  var RAKHI_COLLECTIONS = {};
  RAKHI_COLLECTIONS[RAKHI_FRAGRANCE_HANDLE] = {
    qty: 2,              // units needed to unlock the bundle
    price: 849,          // flat bundle price (display only — Shopify discount does the math)
    badgeLabel: 'Premium',
    premiumCap: 1         // max premium-tagged units allowed in the bundle
  };
  RAKHI_COLLECTIONS[RAKHI_NONFRAGRANCE_HANDLE] = {
    qty: 3,
    price: 599,
    badgeLabel: 'Signature',
    premiumCap: 1
  };
  var RAKHI_CFG = RAKHI_COLLECTIONS[BS_CONFIG.collectionHandle] || RAKHI_COLLECTIONS[RAKHI_FRAGRANCE_HANDLE];

  // ─────────────────────────────────────────────────────────────────
  // STATE — single source of truth for birthday sale cart state
  // ─────────────────────────────────────────────────────────────────
  var BS_STATE = {
    cartItems: [],      // [{ variantId, qty, isPremium }] — birthday sale items only
    normalCount: 0,     // total normal product qty in cart
    premiumCount: 0,    // total premium product qty in cart
    totalCount: 0,      // normalCount + premiumCount
    validCount: 0,      // normalCount + min(premiumCount, RAKHI_CFG.premiumCap) — items that count toward deal
    bsSavings: 0        // total Birthday Sale discount amount (in paise)
  };

  // ─────────────────────────────────────────────────────────────────
  // PRODUCT MAP — variantId → isPremium, fetched from collection API
  // Used by syncState so counts are cart-based, not DOM-based.
  // Cached in sessionStorage to avoid repeat fetches on navigation.
  // ─────────────────────────────────────────────────────────────────
  var BS_PRODUCT_MAP = null;

  function initProductMap() {
    // Fetch BOTH collections, not just the current page's — cart is the
    // single source of truth and can hold mixed Fragrance + Non-fragrance
    // items, so the map needs to recognize both regardless of which
    // collection page built it.
    var CACHE_KEY = 'rakhi_product_map_v2';
    var cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try { BS_PRODUCT_MAP = JSON.parse(cached); return; } catch (e) {}
    }
    var handles = [RAKHI_FRAGRANCE_HANDLE, RAKHI_NONFRAGRANCE_HANDLE];
    Promise.all(handles.map(function (h) {
      return fetch('/collections/' + h + '/products.json?limit=250&fields=variants,tags')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return d.products || []; })
        .catch(function () { return []; });
    })).then(function (results) {
      var map = {};
      results.forEach(function (products, i) {
        var collectionHandle = handles[i];
        products.forEach(function (product) {
          var tags = product.tags || [];
          var isPremium = Array.isArray(tags)
            ? tags.indexOf('premium') !== -1
            : String(tags).split(',').map(function (t) { return t.trim(); }).indexOf('premium') !== -1;
          (product.variants || []).forEach(function (v) {
            map[v.id] = {
              isPremium: isPremium,
              collectionHandle: collectionHandle,
              price: Math.round(parseFloat(v.price) * 100) || 0,
              compareAtPrice: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0
            };
          });
        });
      });
      BS_PRODUCT_MAP = map;
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(map)); } catch (e) {}
      // Re-run syncState (not just updateUI) — BS_STATE.bsSavings/counts may have been
      // computed earlier with an empty map (fetch race), so they need recalculating now
      // that BS_PRODUCT_MAP is actually populated. Only if BS progress bar is visible —
      // avoids late DOM mutations that reset GokWik's MutationObserver debounce and
      // block the checkout button.
      if (document.querySelector('.bs-pb')) syncState();
    });
  }

  // A product counts as premium via EITHER a 'premium' tag OR the
  // metafields.custom.premium_label metafield (card-product.liquid line
  // ~245: data-bs-premium="true" if either is set). BS_PRODUCT_MAP only
  // knows about tags (metafields aren't available from the lightweight
  // /products.json fetch used to build it) — so it silently misses any
  // product that's premium ONLY via the metafield. The grid card's own
  // data-bs-premium attribute is Liquid-computed and already correctly
  // covers both, so prefer it whenever the card is actually in the DOM;
  // only fall back to the (tags-only, less accurate) map when it isn't
  // (filtered out, or a different variant of a multi-variant product).
  // Remembers every variant's DOM-observed premium status for the rest of
  // the session — so that once a card has been seen (e.g. right when it was
  // added to cart), a later filter that removes that exact card from the
  // grid doesn't fall all the way back to the tags-only map and lose the
  // metafield-based premium detection again.
  var _rakhiPremiumSeen = {};

  function isVariantPremium(variantId, mapEntry) {
    var domEl = document.querySelector(
      '.bs-atc-btn[data-variant-id="' + variantId + '"],' +
      '.bs-qty-selector[data-variant-id="' + variantId + '"]'
    );
    var card = domEl && (domEl.closest('.card-wrapper') || domEl.closest('li'));
    // data-bs-premium is only ever rendered when true (no false/absent
    // distinction in the Liquid) — so "card found" is the right signal for
    // "trust the DOM", not "attribute present".
    if (card) {
      var isPrem = !!(card.dataset && card.dataset.bsPremium === 'true');
      _rakhiPremiumSeen[variantId] = isPrem;
      return isPrem;
    }
    if (_rakhiPremiumSeen.hasOwnProperty(variantId)) {
      return _rakhiPremiumSeen[variantId];
    }
    return !!(mapEntry && mapEntry.isPremium);
  }

  // ─────────────────────────────────────────────────────────────────
  // CART HELPERS
  // ─────────────────────────────────────────────────────────────────

  var miniCart = document.querySelector('mini-cart');

  // True only on Rakhi sale collection pages — guards drawer control logic.
  var IS_BS_PAGE = window.location.pathname.indexOf(RAKHI_FRAGRANCE_HANDLE) !== -1 ||
    window.location.pathname.indexOf(RAKHI_NONFRAGRANCE_HANDLE) !== -1;

  // Patch miniCart.renderContents — intercept before drawer opens
  // parsedState from /cart/add.js = single added item (NOT full cart)
  // So use parsedState.variant_id + DOM bsIds to detect BS add
  // and BS_STATE.totalCount (pre-add) + 1 = new total
  if (IS_BS_PAGE && miniCart) {
    var _origRenderContents = miniCart.renderContents.bind(miniCart);
    miniCart.renderContents = function (parsedState) {
      var addedVariantId = String(parsedState && (parsedState.variant_id || parsedState.id) || '');
      // Is this a birthday sale product? Check against page DOM variant IDs
      var isBsProduct = !!document.querySelector(
        '.bs-atc-btn[data-variant-id="' + addedVariantId + '"],' +
        '.bs-qty-selector[data-variant-id="' + addedVariantId + '"]'
      );
      if (isBsProduct) {
        var newTotal = BS_STATE.totalCount + 1;
        if (!shouldOpenDrawer(newTotal)) {
          // Silent: update sections (cart bubble count) but don't open drawer
          miniCart.productId = parsedState.id;
          miniCart.getSectionsToRender().forEach(function (section) {
            var el = document.getElementById(section.elementId || section.id);
            if (el && parsedState.sections && parsedState.sections[section.id]) {
              el.innerHTML = miniCart.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
            }
          });
          return;
        }
      }
      _origRenderContents(parsedState);
    };
  }

  // Keep the native buy-buttons.liquid button's data-variant-id in sync when
  // the user switches variant/shade (Cosmetics quick view etc). It's baked
  // in server-side at render time and never updated again by the theme, so
  // once the shopper picks a different shade the isBsProduct check above
  // stops matching that variant id. Scoped via data-section (not a guessed
  // ancestor) — variant-radios/variant-selects and buy-buttons.liquid's
  // <form id="product-form-{{ section.id }}"> share the same section.id.
  if (IS_BS_PAGE) {
    document.addEventListener('variant:change', function (e) {
      var newVariant = e.detail && e.detail.variant;
      var sectionId = e.target && e.target.dataset && e.target.dataset.section;
      if (!newVariant || !newVariant.id || !sectionId) return;
      var form = document.getElementById('product-form-' + sectionId);
      var btn = form && form.querySelector('.bs-atc-btn[data-variant-id]');
      if (btn) btn.setAttribute('data-variant-id', newVariant.id);
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // NATIVE ATC DRAWER GATE
  //
  // Confirmed via live debugging: the native buy-buttons.liquid <button>
  // flow (used by multi-variant Cosmetics products in Quick View) never
  // reaches the renderContents patch above — whatever internal call opens
  // the drawer for that flow doesn't go through renderContents at all. An
  // earlier attempt to close the drawer AFTER the fact (once BS_STATE
  // resynced) raced against that same internal call and lost unpredictably.
  //
  // Instead, gate the actual open() action itself — cart-drawer.js's
  // MiniCart.open() is what every path (renderContents included) ultimately
  // calls to show the drawer. Patching it here intercepts ALL of them
  // uniformly, regardless of which internal mechanism triggered it.
  //
  // BS_STATE.totalCount may still be one add behind at the exact moment
  // open() fires (cart_updated hasn't resynced yet) — the same reason the
  // renderContents patch above does "+1". _rakhiNativeAtcPending (set on
  // any native Rakhi ATC form submit, cleared once resync catches up or
  // after 5s) tells us whether to apply that same +1 assumption here too.
  var _rakhiNativeAtcPending = false;
  var _rakhiNativeAtcTimer = null;
  // Close the native Quick View modal (Cosmetics "Choose options") once its
  // Add to Cart succeeds — matches how Rakhi's own bs-qv-sheet already
  // auto-closes after its qty-selector "+" adds. Only closes the modal;
  // doesn't touch the drawer-open threshold logic above/below in any way.
  var _rakhiQuickViewDrawerToClose = null;
  if (IS_BS_PAGE) {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.querySelector || !form.querySelector('.bs-atc-btn[data-variant-id]')) return;
      _rakhiNativeAtcPending = true;
      clearTimeout(_rakhiNativeAtcTimer);
      _rakhiNativeAtcTimer = setTimeout(function () { _rakhiNativeAtcPending = false; }, 5000);

      var qvDrawer = form.closest('quick-view-drawer');
      if (qvDrawer) _rakhiQuickViewDrawerToClose = qvDrawer;
    }, true);
  }

  function closePendingQuickViewDrawer() {
    if (!_rakhiQuickViewDrawerToClose) return;
    var drawer = _rakhiQuickViewDrawerToClose;
    _rakhiQuickViewDrawerToClose = null;
    if (typeof drawer.closeMenuDrawer === 'function') {
      // closeMenuDrawer(event, ...) only runs its actual close logic when
      // event !== undefined — event itself is never read inside, just used
      // as a "was this called from a real trigger" check. We're calling it
      // programmatically (no real DOM event to pass), so pass any truthy
      // stand-in — calling with zero args left this a silent no-op before.
      drawer.closeMenuDrawer(true);
    }
  }

  if (IS_BS_PAGE && miniCart && typeof miniCart.open === 'function') {
    var _origMiniCartOpen = miniCart.open.bind(miniCart);
    miniCart.open = function () {
      // Only second-guess this call when we KNOW it's from an untrusted
      // native-form submit (flag set above). Every other caller — including
      // openDrawer()/the qty-selector "+" flow, which goes through
      // renderContents' own already-correct gate right before reaching
      // here — gets a free pass. Re-checking unconditionally here, without
      // that same +1 context, double-guessed an already-correct decision
      // using stale (pre-resync) BS_STATE and wrongly blocked legitimate
      // opens (e.g. Fragrance's 2-qty unlock never opened the drawer).
      if (_rakhiNativeAtcPending) {
        var assumedTotal = BS_STATE.totalCount + 1;
        _rakhiNativeAtcPending = false;
        clearTimeout(_rakhiNativeAtcTimer);
        if (!shouldOpenDrawer(assumedTotal)) return;
      }
      _origMiniCartOpen();
    };
  }

  function fetchConfig(type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/' + type + ', text/' + type
      }
    };
  }

  // Update cart qty — first fetches /cart.js to get line number,
  // then uses line (not variant id) exactly like CartItems.updateQuantity in cart.js
  //
  // Shopify keeps a variant split across multiple cart lines when line item
  // properties differ between /cart/add.js calls (e.g. the first add via the
  // theme's native <add-to-cart> form vs. our own plain-JSON cartAdd() calls).
  // qty here is the desired TOTAL across all of that variant's lines, so find
  // every matching line and adjust the last one so the total comes out right.
  function cartChange(variantId, qty) {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = 0;
        var otherLinesTotal = 0;
        var lastLineQty = 0;
        for (var i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].variant_id) === String(variantId)) {
            if (line) otherLinesTotal += lastLineQty;
            line = i + 1; // 1-based line index
            lastLineQty = cart.items[i].quantity;
          }
        }
        if (!line) return Promise.reject(new Error('BS: variant not in cart'));

        var newLineQty = Math.max(0, qty - otherLinesTotal);

        var sections = miniCart ? miniCart.getSectionsToRender().map(function (s) { return s.id; }) : [];
        return fetch(window.routes.cart_change_url, Object.assign(fetchConfig('javascript'), {
          body: JSON.stringify({
            line: line,
            quantity: newLineQty,
            sections: sections,
            sections_url: window.location.pathname
          })
        })).then(function (r) { return r.json(); });
      });
  }

  // Add to cart with sections — returns Promise with parsedState (for drawer)
  function cartAdd(variantId) {
    var sections = miniCart ? miniCart.getSectionsToRender().map(function (s) { return s.id; }) : [];
    var body = JSON.stringify({
      id: parseInt(variantId),
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname
    });
    return fetch(window.routes.cart_add_url, Object.assign(fetchConfig('javascript'), { body: body }))
      .then(function (r) { return r.json(); });
  }

  // Rakhi has ONE milestone (RAKHI_CFG.qty) — drawer opens once that's hit,
  // and stays opening on every add after (unlike Birthday's 2-then-4 gate).
  function shouldOpenDrawer(newTotal) {
    return newTotal >= RAKHI_CFG.qty;
  }

  // Open mini-cart drawer using existing system
  function openDrawer(parsedState) {
    if (miniCart && parsedState && parsedState.sections) {
      miniCart.renderContents(parsedState);
    } else if (miniCart) {
      miniCart.open();
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // STATE SYNC — fetch cart and update BS_STATE
  // Uses DOM (.bs-qty-selector variantIds) to identify birthday sale
  // items — more reliable than checking product_tags from cart API
  // ─────────────────────────────────────────────────────────────────
  function syncState() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Build variantId → qty map from cart. Sum (not overwrite) because
        // Shopify can keep the same variant split across multiple lines when
        // line item properties differ between /cart/add.js calls.
        var cartQtyMap = {};
        cart.items.forEach(function (item) {
          cartQtyMap[item.variant_id] = (cartQtyMap[item.variant_id] || 0) + item.quantity;
        });

        var bsItems = [];
        var normal = 0;
        var premium = 0;

        var _syncSeen = {};
        document.querySelectorAll('.bs-qty-selector').forEach(function (selector) {
          var variantId = parseInt(selector.dataset.variantId);
          if (!variantId || _syncSeen[variantId]) return;
          var qty = cartQtyMap[variantId] || 0;
          if (qty === 0) return;
          _syncSeen[variantId] = true;
          var card = selector.closest('.card-wrapper') || selector.closest('li');
          var isPremium = !!(card && (
            card.dataset.bsPremium === 'true' || card.querySelector('.plp-badge-premium')
          ));
          bsItems.push({ variantId: variantId, qty: qty, isPremium: isPremium });
          if (isPremium) { premium += qty; } else { normal += qty; }
        });

        // Fallback for non-collection pages: read Liquid-rendered counts from mini-cart
        if (normal === 0 && premium === 0) {
          var countsEl = document.getElementById('bs-cart-counts');
          if (countsEl) {
            normal = parseInt(countsEl.dataset.bsNormal || '0', 10);
            premium = parseInt(countsEl.dataset.bsPremium || '0', 10);
          }
        }

        // Cart is the single source of truth, not the grid DOM. Once
        // BS_PRODUCT_MAP has loaded, trust it completely — no comparison
        // against the DOM-scan above, since that comparison itself was a
        // source of bugs (both landing on 0 in edge cases still picks the
        // wrong one either way). The DOM-scan above is ONLY a fallback for
        // the brief window before this map has finished loading.
        //
        // Also rebuild bsItems (→ BS_STATE.cartItems) here, not just the
        // normal/premium totals — syncAndRestoreSelectors() looks up each
        // qty-selector's variant in BS_STATE.cartItems to decide whether to
        // show it as "in cart". Left as the DOM-scan-only array, any product
        // not currently rendered (not yet loaded via infinite scroll after a
        // filter change, in particular) would never appear in it, so its qty
        // selector stays wrongly stuck on "not in cart" even though it is.
        if (BS_PRODUCT_MAP && Object.keys(BS_PRODUCT_MAP).length > 0) {
          normal = 0; premium = 0;
          bsItems = [];
          cart.items.forEach(function (item) {
            var entry = BS_PRODUCT_MAP[item.variant_id];
            if (!entry || entry.collectionHandle !== BS_CONFIG.collectionHandle) return;
            var isPrem = isVariantPremium(item.variant_id, entry);
            bsItems.push({ variantId: item.variant_id, qty: item.quantity, isPremium: isPrem });
            if (isPrem) { premium += item.quantity; } else { normal += item.quantity; }
          });
        }

        // var savings = 0;
        // cart.items.forEach(function (item) {
        //   if (!item.discounts) return;
        //   item.discounts.forEach(function (d) {
        //     if (d.title && d.title.indexOf('Birthday Sale') !== -1) savings += d.amount;
        //   });
        // });

        // Savings = MRP (compare_at_price) minus what's actually paid, summed only across
        // items that actually HAVE the Birthday Sale discount applied (same scope as
        // Liquid's _item_is_bs in mini-cart-revamp.liquid — collection membership alone
        // isn't enough). Uses final_line_price (falls back to line_price) — NOT item.price
        // — because item.price excludes cart/order-level automatic discounts.
        var savings = 0;
        cart.items.forEach(function (item) {
          var hasBsDiscount = (item.discounts || []).some(function (d) {
            return d.title && d.title.indexOf('Birthday Sale') !== -1;
          });
          if (!hasBsDiscount) return;
          var mapEntry = BS_PRODUCT_MAP && BS_PRODUCT_MAP[item.variant_id];
          var mrpUnit = mapEntry && mapEntry.compareAtPrice > item.price ? mapEntry.compareAtPrice : item.price;
          var mrpLine = mrpUnit * item.quantity;
          var paidLine = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
          if (mrpLine > paidLine) savings += (mrpLine - paidLine);
        });

        BS_STATE.cartItems = bsItems;
        BS_STATE.normalCount = normal;
        BS_STATE.premiumCount = premium;
        BS_STATE.totalCount = normal + premium;
        BS_STATE.validCount = normal + Math.min(premium, RAKHI_CFG.premiumCap);
        BS_STATE.bsSavings = savings;

        updateUI();
        // ── FUTURE: trigger bundle price UI update here ──

        return BS_STATE;
      });
  }

  // ─────────────────────────────────────────────────────────────────
  // PROGRESS BAR + PREMIUM CAP — updateUI() is the single entry point
  // Called after every syncState() and restoreSelectorsFromCart()
  // ─────────────────────────────────────────────────────────────────

  // Copy text per state. TODO: swap in exact PRD copy once confirmed —
  // these are placeholder strings following the established pattern.
  // Exact PRD copy (§7 Progress bar text copies). Same wording pattern works
  // for both collections at every intermediate step — only the "0 added" and
  // "unlocked" strings need per-collection wording.
  //   Fragrance (qty 2):     0 → "Add any 2 & pay just ₹849"
  //                          1 → "Add 1 more to unlock 2 for ₹849"
  //                          2 → "Unlocked! Both products for ₹849"
  //   Non-Fragrance (qty 3): 0 → "Add any 3 & pay just ₹599"
  //                          1 → "Add 2 more to unlock 3 for ₹599"
  //                          2 → "Add 1 more to unlock 3 for ₹599"
  //                          3 → "Unlocked! All 3 products for just ₹599"
  function rakhiPbText(validCount) {
    var qty = RAKHI_CFG.qty;
    var price = RAKHI_CFG.price;

    if (validCount >= qty) {
      return qty === 2
        ? ('Unlocked! Both products for ₹' + price)
        : ('Unlocked! All ' + qty + ' products for just ₹' + price);
    }
    if (validCount === 0) {
      return 'Add any ' + qty + ' & pay just ₹' + price;
    }
    var remaining = qty - validCount;
    return 'Add ' + remaining + ' more to unlock ' + qty + ' for ₹' + price;
  }

  // Rakhi has N milestones (RAKHI_CFG.qty) — dot i unlocks once validCount >= i.
  // validCount already caps premium's contribution at RAKHI_CFG.premiumCap, so
  // no extra premium-gating condition is needed here (unlike Birthday's 2-tier).
  function updateProgressBars(validCount) {
    if (!document.querySelector('.bs-pb')) return;

    var qty = RAKHI_CFG.qty;
    var pct = Math.min(validCount / qty, 1) * 100;
    var text = rakhiPbText(validCount);

    // Mini-cart fill uses --bs-mini-fill CSS variable set on #mini-cart.
    // #mini-cart element persists across section re-renders (only innerHTML changes),
    // so the variable survives and the fill never flashes back to 0%.
    var miniCartEl = document.getElementById('mini-cart');
    if (miniCartEl) miniCartEl.style.setProperty('--bs-mini-fill', pct + '%');

    document.querySelectorAll('.bs-pb').forEach(function (bar) {
      var textEl = bar.querySelector('.bs-pb__text');
      if (textEl) textEl.textContent = text;

      for (var i = 1; i <= qty; i++) {
        var m = bar.querySelector('.bs-pb__milestone--' + i);
        if (m) m.classList.toggle('bs-pb__milestone--unlocked', validCount >= i);
      }

      // All bars: set inline style directly (overrides Liquid default, no flicker)
      var fillEl = bar.querySelector('.bs-pb__fill');
      if (fillEl) fillEl.style.width = pct + '%';
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // PREMIUM LIMIT TOAST
  // ─────────────────────────────────────────────────────────────────
  var _toastTimer = null;

  function showPremiumToast(maxPremium) {
    var toast = document.getElementById('bs-premium-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'bs-premium-toast';
      toast.className = 'bs-premium-toast';
      toast.innerHTML =
        '<p class="bs-premium-toast__text"></p>' +
        '<button class="bs-premium-toast__close" type="button" aria-label="Close">&times;</button>';
      toast.querySelector('.bs-premium-toast__close').addEventListener('click', hidePremiumToast);
      document.body.appendChild(toast);
    }
    toast.querySelector('.bs-premium-toast__text').textContent =
      'Offer valid with ' + maxPremium + ' ' + RAKHI_CFG.badgeLabel + ' product only';
    toast.classList.add('bs-premium-toast--visible');
    clearTimeout(_toastTimer);
    _toastTimer = setTimeout(hidePremiumToast, 3000);
  }

  function hidePremiumToast() {
    var toast = document.getElementById('bs-premium-toast');
    if (toast) toast.classList.remove('bs-premium-toast--visible');
  }

  function updatePremiumCap() {
    // Rakhi cap is flat (RAKHI_CFG.premiumCap, always 1) — unlike Birthday's
    // scaling "+1 per normal product" cap, one premium item fills the slot
    // immediately regardless of how many normal items are in cart.
    // Once the bundle deal is unlocked (validCount >= qty — same threshold
    // used everywhere else: shouldOpenDrawer, rakhiPbText, progress bar
    // milestones), the cap no longer applies — all premium products show
    // again on both collections. RAKHI_CFG/BS_STATE are resolved per the
    // current collection page, so this covers Fragrance and Non-fragrance
    // symmetrically with no extra branching.
    var maxPremium = RAKHI_CFG.premiumCap;
    var isUnlocked = BS_STATE.validCount >= RAKHI_CFG.qty;
    var atCap = !isUnlocked && BS_STATE.premiumCount > 0 && BS_STATE.premiumCount >= maxPremium;

    document.querySelectorAll('.bs-atc-btn').forEach(function (btn) {
      var card = btn.closest('.card-wrapper') || btn.closest('li');
      if (!card) return;
      var isPremium = !!(card.dataset && card.dataset.bsPremium === 'true');
      if (!isPremium) return;

      // Target the li so the grid reflows (no empty gap left behind)
      var li = card.closest('li') || card;

      var wrapper = btn.closest('.card-information__button');
      var isInCart = wrapper && wrapper.classList.contains('bs-in-cart');
      if (isInCart) {
        li.classList.remove('bs-premium-capped');
        return;
      }

      li.classList.toggle('bs-premium-capped', atCap);
    });
  }

  function updatePremiumUpsellVisibility() {
    // Use BS_STATE if already synced; fallback to #bs-cart-counts for non-collection pages
    // where BS_STATE may not be populated yet (async race on cart open)
    var normalCount = BS_STATE.normalCount;
    var premiumCount = BS_STATE.premiumCount;
    if (normalCount === 0 && premiumCount === 0) {
      var countsEl = document.getElementById('bs-cart-counts');
      if (countsEl) {
        normalCount = parseInt(countsEl.dataset.bsNormal || '0', 10);
        premiumCount = parseInt(countsEl.dataset.bsPremium || '0', 10);
      }
    }
    // Same "unlocked → cap lifted" rule as updatePremiumCap() — computed from
    // this function's own local normalCount/premiumCount (not BS_STATE.validCount
    // directly) so it stays correct even on the #bs-cart-counts fallback path above.
    var maxPremium = RAKHI_CFG.premiumCap;
    var validCount = normalCount + Math.min(premiumCount, maxPremium);
    var isUnlocked = validCount >= RAKHI_CFG.qty;
    var atCap = !isUnlocked && premiumCount > 0 && premiumCount >= maxPremium;
    document.querySelectorAll('li[data-bs-upsell-item]').forEach(function (el) {
      if (el.dataset.bsUpsellPremium === 'true') {
        el.style.display = atCap ? 'none' : '';
      }
    });
  }

 // ─────────────────────────────────────────────────────────────────
  // MINI-CART TIER BAR VISIBILITY — hide .bs-pb.bs-pb--mini when the cart's
  // only Birthday Sale collection items are these 2 specific variants; show
  // as soon as any OTHER Birthday Sale product is present (overrides all).
  // Self-contained (own /cart.js fetch) — does not touch updateProgressBars.
  // ─────────────────────────────────────────────────────────────────
  var BS_PB_MINI_HIDE_VARIANT_IDS = ['42104992006190', '41548272304174'];

  function updateMiniPbVisibility() {
    var bars = document.querySelectorAll('.bs-pb.bs-pb--mini');
    var buzzWrappers = document.querySelectorAll('.bs-cart-buzz-wrapper');
    if (!bars.length && !buzzWrappers.length) return;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Hide ONLY when the cart has items and every single one of them is one
        // of the 2 excluded variants — no collection membership check at all.
        // Any other product present (BS or not) means show.
        var hideBar = cart.items.length > 0 && cart.items.every(function (item) {
          return BS_PB_MINI_HIDE_VARIANT_IDS.indexOf(String(item.variant_id)) !== -1;
        });
        bars.forEach(function (bar) {
          bar.style.display = hideBar ? 'none' : '';
        });
        buzzWrappers.forEach(function (wrapper) {
          wrapper.style.display = hideBar ? 'none' : '';
        });
      })
      .catch(function () {});
  }

  function updateUI() {
    updateProgressBars(BS_STATE.validCount);
    updatePremiumCap();
    groupBsCartItems();
    groupRakhiCartItems();
    updatePremiumUpsellVisibility();
  }

  // ─────────────────────────────────────────────────────────────────
  // RAKHI CART ITEM GROUPING — mirrors groupBsCartItems() below but kept as
  // its own function (Rakhi is namespaced separately throughout). Up to TWO
  // independent group boxes (fragrance / non-fragrance) can render at once,
  // each with its own "You are saving ₹X" — driven entirely by data-rakhi-item
  // and data-rakhi-savings attributes already set server-side in
  // mini-cart-revamp.liquid (no extra /cart.js fetch needed here).
  var RAKHI_GROUP_ICON = 'https://cdn.shopify.com/s/files/1/0054/6665/2718/files/image_15.png?v=1787023346';
  // ─────────────────────────────────────────────────────────────────
  var _groupRakhiTimer = null;

  function groupRakhiCartItems() {
    clearTimeout(_groupRakhiTimer);
    _groupRakhiTimer = setTimeout(_doGroupRakhiCartItems, 250);
  }

  function _doGroupRakhiCartItems() {
    var cartList = document.querySelector('.mini-cart__navigation');
    if (!cartList) return;

    // Remove old wrappers — move items back to cartList first
    cartList.querySelectorAll('.rakhi-cart-group-wrapper').forEach(function (oldWrapper) {
      var prevItems = oldWrapper.querySelectorAll('.rakhi-cart-group-items > li');
      prevItems.forEach(function (li) { cartList.insertBefore(li, oldWrapper); });
      oldWrapper.remove();
    });

    // Build BOTH possible group boxes first — extracting their <li> items out
    // of cartList in the process — WITHOUT inserting anything yet. Only once
    // extraction is fully done do we read cartList.firstChild (see below),
    // so that reference can never point at a node that's about to be moved
    // (that race is what caused insertBefore to throw NotFoundError: the
    // captured "first child" was itself one of the items being grouped).
    var wrappers = [];

    ['fragrance', 'non-fragrance'].forEach(function (groupKey) {
      var items = cartList.querySelectorAll('li[data-rakhi-item="' + groupKey + '"]');
      if (!items.length) return;

      var savings = 0;
      items.forEach(function (li) {
        savings += parseInt(li.dataset.rakhiSavings || '0', 10);
      });

      var wrapper = document.createElement('li');
      wrapper.className = 'rakhi-cart-group-wrapper';

      // Icon — pill — icon: the green pill itself stays exactly like
      // Birthday's (plain text, no icon inside it). The two charm icons sit
      // OUTSIDE the pill, one on each side, with a gap — the pill+2 icons
      // together are what's centered on the box's top border (headerRow
      // carries the positioning, .rakhi-cart-group-header keeps just its
      // own pill look).
      var savingsRs = savings > 0 ? ' ₹' + Math.round(savings / 100) : '';

      var headerDiv = document.createElement('div');
      headerDiv.className = 'rakhi-cart-group-header';
      headerDiv.innerHTML = '<span>You are saving' + savingsRs + '</span>';

      var headerRow = document.createElement('div');
      headerRow.className = 'rakhi-cart-group-header-row';
      headerRow.innerHTML =
        '<img src="' + RAKHI_GROUP_ICON + '" width="20" height="20" alt="" class="rakhi-cart-group-header__icon" />';
      headerRow.appendChild(headerDiv);
      headerRow.insertAdjacentHTML('beforeend',
        '<img src="' + RAKHI_GROUP_ICON + '" width="20" height="20" alt="" class="rakhi-cart-group-header__icon" />');

      var innerList = document.createElement('ul');
      innerList.className = 'rakhi-cart-group-items';
      var frag = document.createDocumentFragment();
      items.forEach(function (li) { frag.appendChild(li); });
      innerList.appendChild(frag);

      wrapper.appendChild(headerRow);
      wrapper.appendChild(innerList);

      wrappers.push(wrapper);
    });

    // All group items have now been pulled out of cartList — firstChild is
    // guaranteed to be a real remaining item (or null), never something
    // about to move. Insert fragrance box, then non-fragrance box, both
    // before that same stable point — keeps fragrance-first order.
    var insertionPoint = cartList.firstChild;
    wrappers.forEach(function (wrapper) {
      cartList.insertBefore(wrapper, insertionPoint);
    });

    document.querySelectorAll('.gokwik-checkout button').forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('disabled');
    });
  }

  var _groupBsTimer = null;
  function groupBsCartItems() {
    clearTimeout(_groupBsTimer);
    _groupBsTimer = setTimeout(_doGroupBsCartItems, 250);
  }
  function _doGroupBsCartItems() {
    var cartList = document.querySelector('.mini-cart__navigation');
    if (!cartList) return;

    // Remove old wrapper — move items back to cartList first
    var oldWrapper = cartList.querySelector('.bs-cart-group-wrapper');
    if (oldWrapper) {
      var prevItems = oldWrapper.querySelectorAll('.bs-cart-group-items > li');
      prevItems.forEach(function (li) { cartList.insertBefore(li, oldWrapper); });
      oldWrapper.remove();
    }

    // Re-evaluate data-bs-item using BS_PRODUCT_MAP (authoritative) instead of
    // Liquid discount detection (unreliable when discount re-distributes on qty change)
    // if (BS_PRODUCT_MAP && Object.keys(BS_PRODUCT_MAP).length > 0) {
    //   cartList.querySelectorAll('li[data-variant-id]').forEach(function (li) {
    //     var variantId = parseInt(li.dataset.variantId);
    //     if (BS_PRODUCT_MAP.hasOwnProperty(variantId)) {
    //       li.dataset.bsItem = 'true';
    //     } else {
    //       delete li.dataset.bsItem;
    //     }
    //   });
    // }

    // data-bs-item is trusted as-is from Liquid (mini-cart-revamp.liquid) — it's set
    // only when the line actually has a "Birthday Sale" discount applied, which is the
    // correct scope for this group (collection membership alone is NOT enough — a BS
    // product sitting in the cart without the deal discount applied should not be
    // grouped in here).

    var bsItems = cartList.querySelectorAll('li[data-bs-item="true"]');
    if (bsItems.length === 0) {
      document.querySelectorAll('.gokwik-checkout button').forEach(function (btn) {
        btn.disabled = false;
        btn.classList.remove('disabled');
      });
      return;
    }

    // Wrapper li — acts as the box
    var wrapper = document.createElement('li');
    wrapper.className = 'bs-cart-group-wrapper';

    // Header inside the box
    var headerDiv = document.createElement('div');
    headerDiv.className = 'bs-cart-group-header';
    var savingsRs = BS_STATE.bsSavings > 0 ? ' ₹' + Math.round(BS_STATE.bsSavings / 100) : '';
    headerDiv.innerHTML =
          '<img src="https://cdn.shopify.com/s/files/1/0054/6665/2718/files/Vector.svg?v=1782394202" width="11" height="11" alt="icon" />' +
          '<span>You are saving' + savingsRs + '</span>';

    // Inner list to hold BS items — use fragment so all moves = 1 DOM mutation
    var innerList = document.createElement('ul');
    innerList.className = 'bs-cart-group-items';
    var frag = document.createDocumentFragment();
    bsItems.forEach(function (li) { frag.appendChild(li); });
    innerList.appendChild(frag);

    wrapper.appendChild(headerDiv);
    wrapper.appendChild(innerList);

    // Prepend the whole box to cart — single mutation
    cartList.insertBefore(wrapper, cartList.firstChild);

    // Force-enable checkout button immediately after our DOM mutations —
    // GokWik ka 700ms debounce wait avoid karne ke liye
    document.querySelectorAll('.gokwik-checkout button').forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('disabled');
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // UI HELPERS
  // ─────────────────────────────────────────────────────────────────

  // Find card wrapper containing the ATC/qty for a variantId
  function findCardWrapper(variantId) {
    var atcBtn = document.querySelector('.bs-atc-btn[data-variant-id="' + variantId + '"]');
    return atcBtn ? atcBtn.closest('.card-information__button') : null;
  }

  function showQtySelector(wrapper, qty) {
    var qtySelector = wrapper.querySelector('.bs-qty-selector');
    if (!qtySelector) return;
    qtySelector.querySelector('.bs-qty-count').textContent = qty;
    qtySelector.classList.add('bs-active');
    wrapper.classList.add('bs-in-cart');
  }

  function showAtcBtn(wrapper) {
    var qtySelector = wrapper.querySelector('.bs-qty-selector');
    if (!qtySelector) return;
    qtySelector.classList.remove('bs-active');
    wrapper.classList.remove('bs-in-cart');
  }

  function updateQtyDisplay(wrapper, qty) {
    var countEl = wrapper.querySelector('.bs-qty-count');
    if (countEl) countEl.textContent = qty;
  }

  // ─────────────────────────────────────────────────────────────────
  // EVENT: ATC success → show qty selector + bind buttons
  // Existing <add-to-cart> fires ajaxProduct:added after successful add
  // ─────────────────────────────────────────────────────────────────
  document.addEventListener('ajaxProduct:added', function (e) {
    var product = e.detail && e.detail.product;
    if (!product) return;

    var variantId = product.variant_id || (product.variants && product.variants[0] && product.variants[0].id);
    if (!variantId) return;

    // Update ALL wrappers for this variant (card on collection page + active QV sheet)
    var isBsProduct = false;
    document.querySelectorAll('.bs-atc-btn[data-variant-id="' + variantId + '"]').forEach(function (btn) {
      isBsProduct = true;
      var wrapper = btn.closest('.card-information__button');
      if (!wrapper) return;
      var qtySelector = wrapper.querySelector('.bs-qty-selector');
      if (qtySelector) bindQtySelector(qtySelector);
      showQtySelector(wrapper, 1);
    });

    syncState();
  });

  // ─────────────────────────────────────────────────────────────────
  // BIND QTY BUTTONS — direct listeners on each button
  // Direct binding beats document delegation when ancestors stopPropagation
  // ─────────────────────────────────────────────────────────────────
  function bindQtySelector(qtySelector) {
    if (qtySelector.dataset.bsInit === '1') return;
    qtySelector.dataset.bsInit = '1';

    var wrapper = qtySelector.closest('.card-information__button');
    var variantId = qtySelector.dataset.variantId;
    if (!wrapper || !variantId) return;

    var plusBtn = qtySelector.querySelector('.bs-qty-plus');
    var minusBtn = qtySelector.querySelector('.bs-qty-minus');

    // Disable both buttons + show spinner on selector (prevents race conditions)
    function setLoading(on) {
      qtySelector.classList.toggle('bs-loading', on);
      if (plusBtn) plusBtn.disabled = on;
      if (minusBtn) minusBtn.disabled = on;
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        // Block if premium/signature cap reached for this product.
        // NOTE: badge detection (.plp-badge-premium) currently only fires for
        // tag 'premium' (card-product.liquid) — non-fragrance's 'signature'
        // tag isn't wired to this badge yet; that's Step 3 (premium cap) work.
        var card = qtySelector.closest('.card-wrapper') || qtySelector.closest('li');
        var isPremium = card
          ? !!card.querySelector('.plp-badge-premium')
          : (qtySelector.closest('.bs-qv-sheet') || {}).dataset && qtySelector.closest('.bs-qv-sheet').dataset.premium === 'true';
        if (isPremium) {
          // Same "unlocked → cap lifted" rule as updatePremiumCap() /
          // updatePremiumUpsellVisibility() — once the bundle deal is
          // unlocked (validCount >= qty), the per-product qty limit lifts
          // too, same as the cross-product cap already does. Before unlock,
          // still capped at maxPremium (usually 1) per product.
          var maxPremium = RAKHI_CFG.premiumCap;
          var isUnlocked = BS_STATE.validCount >= RAKHI_CFG.qty;
          if (!isUnlocked && BS_STATE.premiumCount >= maxPremium) {
            showPremiumToast(maxPremium);
            return;
          }
        }

        var currentQty = parseInt(wrapper.querySelector('.bs-qty-count').textContent) || 0;
        var newQty = currentQty + 1;
        setLoading(true);

        var isInQv = !!qtySelector.closest('.bs-qv-sheet');
        cartAdd(variantId)
          .then(function (parsedState) {
            updateQtyDisplay(wrapper, newQty);
            if (isInQv && window.closeQuickView) {
              setTimeout(window.closeQuickView, 300);
            }
            openDrawer(parsedState);
            syncState();
          })
          .finally(function () { setLoading(false); });
      });
    }

    if (minusBtn) {
      minusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var currentQty = parseInt(wrapper.querySelector('.bs-qty-count').textContent) || 0;
        var newQty = currentQty - 1;
        setLoading(true);

        cartChange(variantId, newQty)
          .then(function (parsedState) {
            if (newQty <= 0) {
              showAtcBtn(wrapper);
            } else {
              updateQtyDisplay(wrapper, newQty);
            }
            // Update all cart sections (drawer content + bubble) without opening drawer
            if (miniCart && parsedState && parsedState.sections) {
              miniCart.getSectionsToRender().forEach(function (section) {
                var elementId = section.elementId || section.id;
                var el = document.getElementById(elementId);
                if (el && parsedState.sections[section.id]) {
                  el.innerHTML = miniCart.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
                }
              });
            }
            syncState();
          })
          .finally(function () { setLoading(false); });
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // RESTORE SELECTORS — update all qty selectors + BS_STATE + UI
  // Accepts a Shopify cart object (from /cart.js or cart_updated event)
  // Avoids an extra /cart.js fetch by deriving state from the passed cart
  // ─────────────────────────────────────────────────────────────────
  function restoreSelectorsFromCart(cart) {
    // If cart data not provided in event detail, fall back to a fresh /cart.js fetch
    if (!cart || !cart.items) { syncState(); return; }

    // Sum (not overwrite) — Shopify can keep the same variant split across
    // multiple lines when line item properties differ between add.js calls.
    var cartQtyMap = {};
    cart.items.forEach(function (item) {
      cartQtyMap[item.variant_id] = (cartQtyMap[item.variant_id] || 0) + item.quantity;
    });

    var bsItems = [], normal = 0, premium = 0;
    var _restoreSeen = {};
    document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
      var variantId = parseInt(qtySelector.dataset.variantId);
      if (!variantId || _restoreSeen[variantId]) return;
      var qty = cartQtyMap[variantId] || 0;
      if (qty === 0) return;
      _restoreSeen[variantId] = true;
      var card = qtySelector.closest('.card-wrapper') || qtySelector.closest('li');
      var isPremium = !!(card && (
        card.dataset.bsPremium === 'true' || card.querySelector('.plp-badge-premium')
      ));
      bsItems.push({ variantId: variantId, qty: qty, isPremium: isPremium });
      if (isPremium) { premium += qty; } else { normal += qty; }
    });

    // Fallback for non-collection pages: read Liquid-rendered counts from mini-cart
    if (normal === 0 && premium === 0) {
      var restoreCountsEl = document.getElementById('bs-cart-counts');
      if (restoreCountsEl) {
        normal = parseInt(restoreCountsEl.dataset.bsNormal || '0', 10);
        premium = parseInt(restoreCountsEl.dataset.bsPremium || '0', 10);
      }
    }

    if (BS_PRODUCT_MAP && Object.keys(BS_PRODUCT_MAP).length > 0) {
      normal = 0; premium = 0;
      bsItems = [];
      cart.items.forEach(function (item) {
        var entry = BS_PRODUCT_MAP[item.variant_id];
        if (!entry || entry.collectionHandle !== BS_CONFIG.collectionHandle) return;
        var isPrem = isVariantPremium(item.variant_id, entry);
        bsItems.push({ variantId: item.variant_id, qty: item.quantity, isPremium: isPrem });
        if (isPrem) { premium += item.quantity; } else { normal += item.quantity; }
      });
    }

    // var savings = 0;
    // cart.items.forEach(function (item) {
    //   if (!item.discounts) return;
    //   item.discounts.forEach(function (d) {
    //     if (d.title && d.title.indexOf('Birthday Sale') !== -1) savings += d.amount;
    //   });
    // });
    // Savings = MRP (compare_at_price) minus what's actually paid — see syncState() for
    // the full explanation (same discount-applied scope + final_line_price usage).
    var savings = 0;
    cart.items.forEach(function (item) {
      var hasBsDiscount = (item.discounts || []).some(function (d) {
        return d.title && d.title.indexOf('Birthday Sale') !== -1;
      });
      if (!hasBsDiscount) return;
      var mapEntry = BS_PRODUCT_MAP && BS_PRODUCT_MAP[item.variant_id];
      var mrpUnit = mapEntry && mapEntry.compareAtPrice > item.price ? mapEntry.compareAtPrice : item.price;
      var mrpLine = mrpUnit * item.quantity;
      var paidLine = typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
      if (mrpLine > paidLine) savings += (mrpLine - paidLine);
    });

    BS_STATE.cartItems = bsItems;
    BS_STATE.normalCount = normal;
    BS_STATE.premiumCount = premium;
    BS_STATE.totalCount = normal + premium;
    BS_STATE.validCount = normal + Math.min(premium, RAKHI_CFG.premiumCap);
    BS_STATE.bsSavings = savings;

    // Restore qty selector UI
    document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
      var variantId = parseInt(qtySelector.dataset.variantId);
      var wrapper = qtySelector.closest('.card-information__button');
      if (!wrapper) return;
      var qty = cartQtyMap[variantId] || 0;
      if (qty > 0) { showQtySelector(wrapper, qty); }
      else { showAtcBtn(wrapper); }
    });

    updateUI();
    closePendingQuickViewDrawer();
  }

  // Fetch cart then restore (used on page load)
  function syncAndRestoreSelectors() {
    syncState().then(function () {
      document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
        var variantId = parseInt(qtySelector.dataset.variantId);
        var wrapper = qtySelector.closest('.card-information__button');
        if (!wrapper) return;

        var found = null;
        BS_STATE.cartItems.forEach(function (item) {
          if (item.variantId === variantId) found = item;
        });

        if (found) {
          showQtySelector(wrapper, found.qty);
        } else {
          showAtcBtn(wrapper);
        }
      });

      // syncState() already calls updatePremiumCap() once (via updateUI()),
      // but that happens BEFORE the loop above has a chance to add the
      // 'bs-in-cart' class to any card being seen for the first time this
      // cycle (e.g. right after a filter swaps in fresh grid HTML) — so a
      // premium product that's genuinely in the cart still reads as
      // isInCart=false at that point and gets capped/hidden, with nothing
      // correcting it afterward since this loop runs after that call. Counts
      // (BS_STATE.premiumCount etc.) are untouched here — only re-running the
      // cap check now that 'bs-in-cart' is guaranteed correct for every card
      // currently on the page.
      updatePremiumCap();
    });
  }

  // ─────────────────────────────────────────────────────────────────
  // INIT — bind buttons + sync on page load
  // ─────────────────────────────────────────────────────────────────
  function init() {
    // Fetches BS_PRODUCT_MAP (variant → isPremium/price/compareAtPrice). Cheap on
    // repeat init() calls — cached in sessionStorage, only fetches once per session.
    initProductMap();
    document.querySelectorAll('.bs-qty-selector').forEach(function (qtySelector) {
      bindQtySelector(qtySelector);
    });
    syncAndRestoreSelectors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after infinite scroll / filter
  // 'theme:infinite-scroll:loaded' is never actually dispatched anywhere in
  // this theme (assets/infinite-scroll.js just does a plain
  // insertAdjacentHTML, no event) — kept here in case a future version adds
  // it, but it's currently dead. Real fix: watch #product-grid directly for
  // new cards being appended (infinite scroll's actual mechanism) and re-run
  // init() ourselves — this is what actually re-binds qty-selectors and
  // restores their "in cart" state for products that scroll into view AFTER
  // a filter change, which previously never happened at all.
  document.addEventListener('theme:infinite-scroll:loaded', init);
  document.addEventListener('collection:updated', init);

  (function () {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var _gridInitTimer = null;
    new MutationObserver(function (mutations) {
      var hasNewCard = mutations.some(function (m) {
        return m.addedNodes && Array.prototype.some.call(m.addedNodes, function (n) {
          return n.nodeType === 1 && (n.matches('li') || n.querySelector('.bs-qty-selector, .bs-atc-btn'));
        });
      });
      if (!hasNewCard) return;
      clearTimeout(_gridInitTimer);
      _gridInitTimer = setTimeout(init, 50);
    }).observe(grid, { childList: true });
  })();

  // ─────────────────────────────────────────────────────────────────
  // EXTERNAL CART CHANGES
  // custom-event-cart-update.js fires window 'cart_updated' after
  // every /cart/* request — detail already has fresh cart data,
  // use it directly to avoid a redundant /cart.js fetch
  // ─────────────────────────────────────────────────────────────────
  // cart_updated fires after any /cart/ API call (CartWatcher).
  // restoreSelectorsFromCart handles both state sync + selector UI using event cart data.
  window.addEventListener('cart_updated', function (e) {
    restoreSelectorsFromCart(e.detail);
  });

  document.addEventListener('cartdrawer:opened', function () {
    syncState().then(function () {
      groupBsCartItems();
      updatePremiumUpsellVisibility();
    });
  });

  document.addEventListener('cart-recommendations:updated', function () {
    updatePremiumUpsellVisibility();
  });

  // Fix CartItems.enableLoading — uses DOM order which breaks after groupBsCartItems() reorders li elements.
  // Patch: find loading overlay via data-index of quantity input instead of DOM position.
  customElements.whenDefined('cart-items').then(function () {
    var Proto = customElements.get('cart-items').prototype;
    Proto.enableLoading = function (line) {
      var cartItemsEl = document.getElementById('main-cart-items');
      if (cartItemsEl) cartItemsEl.classList.add('cart__items--disabled');
      var input = this.querySelector('[name="updates[]"][data-index="' + line + '"]');
      if (input) {
        var li = input.closest('li');
        if (li) {
          var overlay = li.querySelector('.loading-overlay');
          if (overlay) overlay.classList.remove('hidden');
        }
      }
      document.activeElement.blur();
      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
    };
  });

  // ─────────────────────────────────────────────────────────────────
  // QUICK VIEW — bottom sheet open/close + carousel + ATC handler
  // ─────────────────────────────────────────────────────────────────
  var _qvPanel   = document.getElementById('bs-qv-panel');
  var _qvOverlay = document.getElementById('bs-qv-overlay');
  var _qvClose   = document.getElementById('bs-qv-close');
  var _qvActiveHandle = null;

  function openQuickView(handle) {
    if (!_qvPanel || !_qvOverlay) return;

    // Hide previously active sheet
    if (_qvActiveHandle) {
      var prev = document.getElementById('bs-qv-' + _qvActiveHandle);
      if (prev) prev.classList.remove('bs-qv-active');
    }

    var sheet = document.getElementById('bs-qv-' + handle);
    if (!sheet) return;

    sheet.classList.add('bs-qv-active');
    sheet.removeAttribute('aria-hidden');
    _qvActiveHandle = handle;

    sheet.scrollTop = 0;

    _qvOverlay.classList.add('bs-qv-open');
    _qvOverlay.removeAttribute('aria-hidden');
    _qvPanel.classList.add('bs-qv-open');
    _qvPanel.removeAttribute('aria-hidden');

    document.body.style.overflow = 'hidden';

    // Re-init fragrance tab indicator (was hidden, offsetWidth was 0 on load)
    requestAnimationFrame(function () {
      window.dispatchEvent(new Event('resize'));
    });

    _initQvCarousel(sheet);
  }

  function closeQuickView() {
    if (!_qvPanel || !_qvOverlay) return;
    _qvOverlay.classList.remove('bs-qv-open');
    _qvOverlay.setAttribute('aria-hidden', 'true');
    _qvPanel.classList.remove('bs-qv-open');
    _qvPanel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (_qvActiveHandle) {
      var sheet = document.getElementById('bs-qv-' + _qvActiveHandle);
      if (sheet) {
        sheet.classList.remove('bs-qv-active');
        sheet.setAttribute('aria-hidden', 'true');
      }
      _qvActiveHandle = null;
    }
  }
  window.closeQuickView = closeQuickView;

  // Initialise carousel scroll + dot sync for a given sheet
  function _initQvCarousel(sheet) {
    var imagesEl = sheet.querySelector('.bs-qv-images');
    var dotsEl   = sheet.querySelector('.bs-qv-dots');
    if (!imagesEl) return;

    // Reset scroll to first image
    imagesEl.scrollLeft = 0;

    if (!dotsEl) return;
    var dots = dotsEl.querySelectorAll('.bs-qv-dot');
    if (!dots.length) return;

    // Prevent binding twice
    if (imagesEl.dataset.qvInit === '1') {
      dots.forEach(function (d) { d.classList.toggle('bs-qv-dot--active', d.dataset.index === '0'); });
      return;
    }
    imagesEl.dataset.qvInit = '1';

    function setActiveDot(idx) {
      dots.forEach(function (d) {
        d.classList.toggle('bs-qv-dot--active', parseInt(d.dataset.index) === idx);
      });
    }

    // Scroll sync → dots (offsetLeft-based for peek carousel)
    imagesEl.addEventListener('scroll', function () {
      var slides = imagesEl.children;
      var step = slides.length > 1 ? slides[1].offsetLeft : (slides[0] ? slides[0].offsetWidth : imagesEl.offsetWidth);
      var idx = Math.round(imagesEl.scrollLeft / step);
      setActiveDot(idx);
    }, { passive: true });

    // Dot click → scroll using slide's offsetLeft so peek gap is respected
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.index);
        var slide = imagesEl.children[idx];
        imagesEl.scrollTo({ left: slide ? slide.offsetLeft : idx * imagesEl.offsetWidth, behavior: 'smooth' });
        setActiveDot(idx);
      });
    });
  }

  // Card click → open QV (skip ATC / qty selector area / the "Choose
  // options" quick-view-button). Excluding the button itself isn't enough —
  // quick-view.js's QuickViewButton programmatically does
  // drawer.querySelector('summary').click() to open the NATIVE
  // quick-view-drawer, and that <quick-view-drawer><details><summary> lives
  // OUTSIDE .card-information__button (a sibling in the card markup, after
  // it closes) and outside <quick-view-button> too — so that synthetic click
  // bubbles to document unexcluded and matches [data-bs-qv] on the card
  // wrapper, opening Rakhi's bs-qv-sheet on top of the native drawer.
  // Excluding quick-view-drawer directly stops that second click from ever
  // reaching openQuickView(), same as .bs-atc-btn is already excluded.
  document.addEventListener('click', function (e) {
    if (e.target.closest('.card-information__button')) return;
    if (e.target.closest('quick-view-button')) return;
    if (e.target.closest('quick-view-drawer')) return;
    var trigger = e.target.closest('[data-bs-qv]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    openQuickView(trigger.dataset.bsQv);
  });

  // Close on overlay click or close button
  if (_qvOverlay) _qvOverlay.addEventListener('click', closeQuickView);
  if (_qvClose)   _qvClose.addEventListener('click', closeQuickView);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && _qvActiveHandle) closeQuickView();
  });

  // Close quick view shortly after ATC/qty+ click — before cart drawer opens
  if (_qvPanel) {
    _qvPanel.addEventListener('click', function (e) {
      if (e.target.closest('.bs-atc-btn') || e.target.closest('.bs-qty-plus')) {
        setTimeout(closeQuickView, 1000);
      }
    });
  }

})();

// ── Sale Countdown Timer ─────────────────────────────────────────────────────
// Supports multiple simultaneous .bs-timer instances (page-level from
// bs-sale-timer-section.liquid + the mini-cart drawer's copy rendered with
// id_suffix: 'cart' in mini-cart-revamp.liquid) — each gets its own
// independent tick loop with scoped lookups, so duplicate/suffixed IDs never
// clash. The drawer's HTML is fetched + injected into the DOM well after this
// script runs (Shopify Section Rendering API), so a one-time querySelectorAll
// at load wouldn't find it — a MutationObserver picks up any .bs-timer added
// later, anywhere on the page.
(function () {
  function initTimer(el) {
    if (el._bsTimerInited) return;
    el._bsTimerInited = true;

    // Read config from data attributes (set in Liquid snippet)
    var saleEnd   = new Date(el.dataset.saleEnd);
    var buzzFrom  = el.dataset.buzzFrom;   // 'YYYY-MM-DD'
    var buzzTo    = el.dataset.buzzTo;     // 'YYYY-MM-DD'
    var buzzSh    = parseInt(el.dataset.buzzSh, 10);  // start hour (20)
    var buzzSm    = parseInt(el.dataset.buzzSm, 10);  // start min  (0)
    var buzzEh    = parseInt(el.dataset.buzzEh, 10);  // end hour   (21)
    var buzzEm    = parseInt(el.dataset.buzzEm, 10);  // end min    (20)

    var labelEl = el.querySelector('.bs-timer__label');
    var dotEl   = el.querySelector('.bs-timer__dot');
    var subEl   = el.querySelector('.bs-timer__sub');
    var daysEl  = el.querySelector('[id^="bs-timer-days"]');
    var hrsEl   = el.querySelector('[id^="bs-timer-hrs"]');
    var minEl   = el.querySelector('[id^="bs-timer-min"]');
    var secEl   = el.querySelector('[id^="bs-timer-sec"]');

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    // Get IST date string 'YYYY-MM-DD' for a given JS Date
    function istDateStr(date) {
      var ist = new Date(date.getTime() + (5.5 * 3600000));
      return ist.toISOString().slice(0, 10);
    }

    // Check if now is inside the buzz window for today
    function getBuzzTarget() {
      var now     = new Date();
      var today   = istDateStr(now);

      if (today < buzzFrom || today > buzzTo) return null;

      // Build today's buzz start/end in IST
      var startStr = today + 'T' + pad(buzzSh) + ':' + pad(buzzSm) + ':00+05:30';
      var endStr   = today + 'T' + pad(buzzEh) + ':' + pad(buzzEm) + ':00+05:30';
      var start    = new Date(startStr);
      var end      = new Date(endStr);

      if (now >= start && now < end) return end;
      return null;
    }

    var _lastMode = null;

    function tick() {
      var now        = new Date();
      var buzzTarget = getBuzzTarget();
      var target, mode;

      if (buzzTarget) {
        target = buzzTarget;
        mode   = 'buzz';
      } else {
        target = saleEnd;
        mode   = 'sale';
      }

      // Update label only on mode switch (avoid DOM thrash every second)
      if (mode !== _lastMode) {
        if (mode === 'buzz') {
          labelEl.textContent      = 'Buzz Offer Ends';
          labelEl.style.color      = '#ff9c1c';
          dotEl.style.background   = '#ff9c1c';
          subEl.textContent        = 'Extra ₹80 off — Hurry!';
        } else {
          labelEl.textContent      = 'Sale Ends In';
          labelEl.style.color      = '#de1612';
          dotEl.style.background   = '#de1612';
          subEl.textContent        = "Don't miss out";
        }
        _lastMode = mode;
      }

      var diff = Math.max(0, target - now);
      var days = Math.floor(diff / 86400000);
      var hrs  = Math.floor((diff % 86400000) / 3600000);
      var mins = Math.floor((diff % 3600000)  / 60000);
      var secs = Math.floor((diff % 60000)    / 1000);

      daysEl.textContent = pad(days);
      hrsEl.textContent  = pad(hrs);
      minEl.textContent  = pad(mins);
      secEl.textContent  = pad(secs);

      if (diff > 0) setTimeout(tick, 1000);
    }

    tick();
  }

  function scanForTimers() {
    document.querySelectorAll('.bs-timer').forEach(initTimer);
  }

  scanForTimers();
  new MutationObserver(scanForTimers).observe(document.body, { childList: true, subtree: true });
})();

// ── Rakhi Sale Timer ─────────────────────────────────────────────────────────
// Was previously an inline <script> inside snippets/rakhi-sale-timer.liquid
// itself, guarded by a window.__rakhiTimerBooted flag so only the FIRST
// rendered instance set up the scan/observer. That flag only ever got set if
// a real page-load actually parsed and ran that <script> tag — but the cart
// drawer's copy (mini-cart-revamp.liquid, id_suffix: 'cart') is delivered via
// innerHTML (Section Rendering API / MiniCart.renderContents), and browsers
// never execute <script> tags injected that way. So on any page where the
// cart-drawer timer was the FIRST (or only) .rakhi-timer to ever appear in
// that page's lifetime — i.e. any page without a page-level Rakhi Sale Timer
// section — nothing ever booted it, leaving it stuck at 00:00:00. Moving
// this to a real external script (this file, loaded normally in theme.liquid
// on every page) fixes it the same way Birthday's .bs-timer above already
// does it: scan once at load, then a MutationObserver catches the timer
// whenever it's later injected, regardless of what page or mechanism added it.
(function () {
  var IST_OFFSET = 5.5 * 3600000;

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  // Next recurring boundary as a real UTC timestamp. Boundaries are aligned to
  // IST midnight and step by intervalMs. So interval 12h → resets 12AM/12PM
  // IST, 8h → 12AM/8AM/4PM, 24h → once daily at 12AM.
  function nextBoundary(now, intervalMs) {
    var istNowMs = now.getTime() + IST_OFFSET;
    var istMidnightMs = Math.floor(istNowMs / 86400000) * 86400000;
    var elapsed = istNowMs - istMidnightMs;
    var k = Math.floor(elapsed / intervalMs) + 1;
    var boundaryIstMs = istMidnightMs + (k * intervalMs);
    return boundaryIstMs - IST_OFFSET;
  }

  function initTimer(el) {
    if (el._rakhiInited) return;
    el._rakhiInited = true;

    var saleEnd = new Date(el.dataset.saleEnd).getTime();
    var intervalMs = (parseFloat(el.dataset.intervalHours) || 12) * 3600000;
    var hrsEl = el.querySelector('[id^="rakhi-timer-hrs"]');
    var minEl = el.querySelector('[id^="rakhi-timer-min"]');
    var secEl = el.querySelector('[id^="rakhi-timer-sec"]');

    function tick() {
      var now = Date.now();

      if (now >= saleEnd) {
        el.style.display = 'none';
        return;
      }

      var target = Math.min(nextBoundary(new Date(now), intervalMs), saleEnd);
      var diff = Math.max(0, target - now);

      var hrs = Math.floor(diff / 3600000);
      var mins = Math.floor((diff % 3600000) / 60000);
      var secs = Math.floor((diff % 60000) / 1000);

      hrsEl.textContent = pad(hrs);
      minEl.textContent = pad(mins);
      secEl.textContent = pad(secs);

      setTimeout(tick, 1000);
    }

    tick();
  }

  function scanForRakhiTimers() {
    document.querySelectorAll('.rakhi-timer').forEach(initTimer);
  }

  scanForRakhiTimers();
  new MutationObserver(scanForRakhiTimers).observe(document.body, { childList: true, subtree: true });
})();

// ── Cart Buzz Timer ──────────────────────────────────────────────────────────
(function () {
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function istDateStr(date) {
    var ist = new Date(date.getTime() + (5.5 * 3600000));
    return ist.toISOString().slice(0, 10);
  }

  function tick() {
    var el = document.getElementById('bs-cart-buzz');
    if (!el) { setTimeout(tick, 1000); return; }

    var buzzFrom = el.dataset.buzzFrom;
    var buzzTo   = el.dataset.buzzTo;
    var buzzSh   = parseInt(el.dataset.buzzSh, 10);
    var buzzSm   = parseInt(el.dataset.buzzSm, 10);
    var buzzEh   = parseInt(el.dataset.buzzEh, 10);
    var buzzEm   = parseInt(el.dataset.buzzEm, 10);

    var now   = new Date();
    var today = istDateStr(now);

    if (today < buzzFrom || today > buzzTo) {
      el.style.display = 'none';
      setTimeout(tick, 5000);
      return;
    }

    var start = new Date(today + 'T' + pad(buzzSh) + ':' + pad(buzzSm) + ':00+05:30');
    var end   = new Date(today + 'T' + pad(buzzEh) + ':' + pad(buzzEm) + ':00+05:30');

    if (now < start || now >= end) {
      el.style.display = 'none';
      setTimeout(tick, 5000);
      return;
    }

    el.style.display = 'flex';

    var minEl = document.getElementById('bs-cart-buzz-min');
    var secEl = document.getElementById('bs-cart-buzz-sec');
    var diff  = Math.max(0, end - now);
    if (minEl) minEl.textContent = pad(Math.floor(diff / 60000));
    if (secEl) secEl.textContent = pad(Math.floor((diff % 60000) / 1000));

    if (diff > 0) {
      setTimeout(tick, 1000);
    } else {
      el.style.display = 'none';
    }
  }

  tick(); // deferred script hai — DOM already ready hai, direct call safe hai
})();

// ── Lottie init (idempotent — safe to call on every cart update) ──
window.bsInitLottie = function (milestoneEl) {
  var lottieEl     = milestoneEl.querySelector('.bs-pb__lottie');
  var lottieTickEl = milestoneEl.querySelector('.bs-pb__lottie--tick');
  if (!lottieEl || lottieEl._bsLottieInited) return;
  var src     = lottieEl.dataset.lottieSrc;
  var tickSrc = lottieTickEl ? lottieTickEl.dataset.lottieSrc : null;
  if (!src || src === 'LOTTIE_JSON_CDN_URL') return;
  lottieEl._bsLottieInited = true;

  function loadTickAnim() {
    if (!lottieTickEl || !tickSrc) return;
    lottieTickEl.style.display = 'block';
    var circleEl = lottieTickEl.closest('.bs-pb__milestone-circle');
    if (circleEl) circleEl.classList.add('bs-tick-active');
    fetch(tickSrc)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        function startTick() {
          var tickAnim = window.lottie.loadAnimation({
            container: lottieTickEl,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: data
          });
          setTimeout(function () {
            tickAnim.pause(); // freeze on last frame
          }, 1500);
        }
        if (window.lottie) { startTick(); }
        else {
          var t = setInterval(function () {
            if (window.lottie) { clearInterval(t); startTick(); }
          }, 100);
        }
      })
      .catch(function () {});
  }

  function loadAnim(data) {
    var anim = window.lottie.loadAnimation({
      container: lottieEl,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: data
    });
    setTimeout(function () {
      anim.pause();      // pehla pause on last frame
      loadTickAnim();    // phir doosra start
    }, 1500);
  }

  fetch(src)
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (window.lottie) {
        loadAnim(data);
      } else {
        var t = setInterval(function () {
          if (window.lottie) { clearInterval(t); loadAnim(data); }
        }, 100);
      }
    })
    .catch(function () {});
};

// ── Milestone confetti → Lottie sequence ──
var _bsConfettiLastFired = 0;
window.bsPlayMilestoneAnim = function (milestoneEl) {
  var confettiEl = milestoneEl.querySelector('.bs-pb__icon--confetti');
  var lottieEl   = milestoneEl.querySelector('.bs-pb__lottie');

  // Step 1: confetti centered inside cart drawer — 3s cooldown prevents double-fire
  var now = Date.now();
  if (confettiEl && confettiEl.src && confettiEl.src.indexOf('CONFETTI_GIF_CDN_URL') === -1 && (now - _bsConfettiLastFired > 3000)) {
    _bsConfettiLastFired = now;
    var drawerEl = document.getElementById('mini-cart') || document.querySelector('.cart-drawer');
    var target = drawerEl || document.body;
    var gif = document.createElement('img');
    gif.src = confettiEl.src;
    gif.style.cssText = 'position:absolute;z-index:99999;pointer-events:none;' +
      'left:50%;top:20%;transform:translate(-50%,-50%);width:auto;height:auto;';
    target.appendChild(gif);
    setTimeout(function () { gif.parentNode && gif.parentNode.removeChild(gif); }, 1500);
  }

  // Step 2: init lottie after confetti (2.5s delay, then bsInitLottie handles it)
  setTimeout(function () {
    if (window.bsInitLottie) window.bsInitLottie(milestoneEl);
  }, 1500);
};

// ── Header cart-count badge refresh on bfcache restore ─────────────────────
// QA repro: add a product on a Rakhi collection, navigate to a different
// collection, add another product there (badge correctly shows 2), then hit
// the browser Back button — badge shows 1 again. Root cause: browser
// back/forward navigation often restores the page from bfcache (instant,
// no fresh request, no JS re-runs) instead of reloading it — so the page
// you're going back to still has the header markup frozen from whenever it
// was last actually rendered (before the 2nd add-to-cart happened on the
// other page). Deliberately placed here (not assets/global.js, a shared
// file every page depends on) to keep this fully scoped to Rakhi's own file,
// which already loads globally anyway.
// event.persisted is only true for an actual bfcache restore — false on
// every normal page load — so this never adds an extra request on regular
// navigation, only the specific back/forward-into-cache case. Refetches just
// the two small header sections (Section Rendering API), not a full reload.
// window.addEventListener('pageshow', function (event) {
//   if (!event.persisted) return;
//   fetch('/?sections=cart-icon-bubble,mobile-cart-icon-bubble')
//     .then(function (r) { return r.json(); })
//     .then(function (sections) {
//       ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach(function (id) {
//         var el = document.getElementById(id);
//         var html = sections[id];
//         if (!el || !html) return;
//         var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
//         if (fresh) el.innerHTML = fresh.innerHTML;
//       });
//     })
//     .catch(function () {});
// });
